"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const FinancePage = () => {
    const { t, translationsLoaded } = useComponentTranslations('Finance');
    const ft = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems: Array<{ title: string; icon: string }> = [
        { title: ft('finExpertise1'), icon: 'carbon:analytics' },
        { title: ft('finExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: ft('finExpertise3'), icon: 'carbon:rule' },
        { title: ft('finExpertise4'), icon: 'carbon:security' },
        { title: ft('finExpertise5'), icon: 'carbon:cloud-app' },
        { title: ft('finExpertise6'), icon: 'carbon:blockchain' },
        { title: ft('finExpertise7'), icon: 'carbon:data-refinery' },
        { title: ft('finExpertise8'), icon: 'carbon:chart-line-data' },
        { title: ft('finExpertise9'), icon: 'carbon:user-profile' },
    ];

    // Данные для аккордеона (Примеры решений)
    const useCasesForAccordion: Array<{ title: string; description: string; backgroundImage: string }> = [
        { title: ft('finUseCase1'), description: ft('finUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: ft('finUseCase2'), description: ft('finUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: ft('finUseCase3'), description: ft('finUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: ft('finUseCase4'), description: ft('finUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    // Данные для чередующегося макета (Услуги)
    const servicesForAlternating: Array<{ title: string; description: string; image: string }> = [
        { title: ft('finService1Title'), description: ft('finService1Desc'), image: '/images/finance.jpg' },
        { title: ft('finService2Title'), description: ft('finService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: ft('finService3Title'), description: ft('finService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: ft('finService4Title'), description: ft('finService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: ft('finService5Title'), description: ft('finService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems: Array<{ question: string; answer: string }> = [
        { question: ft('finFaq1q'), answer: ft('finFaq1a') },
        { question: ft('finFaq2q'), answer: ft('finFaq2a') },
        { question: ft('finFaq3q'), answer: ft('finFaq3a') },
        { question: ft('finFaq4q'), answer: ft('finFaq4a') },
        { question: ft('finFaq5q'), answer: ft('finFaq5a') },
    ];

    return (
        <div className="relative min-h-screen bg-transparent text-white">
            {/* Hero-блок */}
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {ft('finHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-sm text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {ft('finHeroSubtitle')}
                </motion.p>
            </header>

            {/* Проблемы индустрии */}
            <section className="w-full py-24 glass-section glass-border-y">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{ft('finPainTitle')}</h2>
                    <p className="text-sm text-white/70 text-center max-w-4xl mx-auto">
                        {ft('finPainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Наше решение / Expertise */}
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{ft('finExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 glass-border-strong glass-rounded glass-card flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                style={{ willChange: "transform, opacity" }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={ft('finServicesTitle')} useCases={servicesForAlternating} />
            </div>

            {/* Accordion with Use Cases */}
            <ServicesAccordion title={ft('finUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={ft('finTechTitle')} t={t} />
                <IndustrialsForm label={ft('finCtaTitle')} />

                {/* FAQ */}
                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="uppercase text-center mb-12">{ft('finFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 glass-border-strong glass-rounded glass-card">
                                <summary className="text-sm cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>

            </div>
        </div>
    );
};

export default FinancePage;
